import { useMemo } from 'react';
import {
  BaseTable,
  type TableColumnDefinition,
} from '@amzn/gsce-shared-react-lib/components/table';
import { Col } from '@amzn/stencil-react-components/layout';
import {
  SkeletonText,
  SkeletonTextVariant,
} from '@amzn/stencil-react-components/skeleton';
import { TableSpacing } from '@amzn/stencil-react-components/table';

import { RISK_TABLE_SKELETON_ROW_COUNT } from '../logic/riskScorePresentation';

interface SkeletonRow {
  readonly rowId: string;
}

const NO_ROWS: SkeletonRow[] = [];
const SKELETON_TABLE_ID = 'risk-engine-page-skeleton';

/** Blank-header placeholder columns of equal width; the shared table fills them with shimmer. */
export function buildPlaceholderColumns(
  columnCount: number,
): TableColumnDefinition<SkeletonRow>[] {
  const width = `${100 / columnCount}%`;
  return Array.from({ length: columnCount }, (_, index) => ({
    id: `skeleton-${index}`,
    header: '',
    width,
    cell: () => null,
  }));
}

interface RiskEnginePageSkeletonProps {
  /** Columns in the table the page will show, so the placeholder matches its shape. */
  readonly columnCount: number;
  readonly dataTestId: string;
}

/**
 * Stand-in for a whole Risk Engine page while its translations load, so raw message keys never
 * reach the screen: a title line, a subtitle line, and the shared table's own loading skeleton with
 * blank headers (no localized header text exists yet).
 */
export function RiskEnginePageSkeleton({
  columnCount,
  dataTestId,
}: RiskEnginePageSkeletonProps): JSX.Element {
  const columns = useMemo(() => buildPlaceholderColumns(columnCount), [columnCount]);

  return (
    <Col
      aria-busy
      dataTestId={dataTestId}
      gridGap="dimensions.spacing.300"
      minWidth={0}
      padding={{
        top: 'dimensions.spacing.300',
        bottom: 'dimensions.spacing.600',
        left: 'dimensions.spacing.200',
        right: 'dimensions.spacing.200',
      }}
      width="100%"
    >
      <Col gridGap="dimensions.spacing.100">
        <SkeletonText variant={SkeletonTextVariant.HeadingLG} width="40%" />
        <SkeletonText variant={SkeletonTextVariant.BodyMD} width="60%" />
      </Col>
      <BaseTable
        tableId={SKELETON_TABLE_ID}
        items={NO_ROWS}
        columnDefinitions={columns}
        trackBy="rowId"
        loading
        skeletonRowCount={RISK_TABLE_SKELETON_ROW_COUNT}
        spacing={TableSpacing.Reduced}
        shouldScrollHorizontally={false}
        fullWidth
        showSettings={false}
      />
    </Col>
  );
}
